// CRITICAL: React must be imported FIRST to ensure it's available before other modules
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import App from './App.tsx';
import './index.css';
import { CleanupManager } from './lib/cleanup-utils';
import { performanceMonitor } from './lib/performance-monitor';
import { registerServiceWorker } from './lib/service-worker';
import { getSentryConfig } from './lib/sentry-config';
import { getMixpanelConfig } from './lib/mixpanel-config';
import { supabase } from '@/integrations/supabase/client';
import { safeMixpanelTrack } from './lib/mixpanel-utils';

// Get Sentry configuration based on environment
const sentryConfig = getSentryConfig();

// Get Mixpanel configuration based on environment
const mixpanelConfig = getMixpanelConfig();

// Initialize Sentry after page load to reduce critical path latency
// This defers Sentry network requests until after initial render
const initSentry = () => {
  if (sentryConfig.dsn) {
    Sentry.init({
      dsn: sentryConfig.dsn,
      environment: sentryConfig.environment,
      serviceType: sentryConfig.serviceType,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // Set sample rate for performance monitoring
      tracesSampleRate: process.env.NODE_ENV === 'development' ? 0.1 : 1.0, // Reduce in dev to avoid rate limits
      // Set sample rate for profiling
      profilesSampleRate: process.env.NODE_ENV === 'development' ? 0.1 : 1.0, // Reduce in dev
      // Session Replay configuration
      replaysSessionSampleRate: process.env.NODE_ENV === 'development' ? 0.1 : 0.1, // Reduce in dev
      replaysOnErrorSampleRate: process.env.NODE_ENV === 'development' ? 0.5 : 1.0, // Reduce in dev to avoid rate limits
      // Enable automatic instrumentation
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Additional replay configuration options
          maskAllText: false, // Set to true to mask all text for privacy
          blockAllMedia: false, // Set to true to block all media for privacy
        }),
        Sentry.feedbackIntegration({
          colorScheme: "system",
          isNameRequired: true,
          isEmailRequired: true,
          // Disable the injection of the default widget
          autoInject: false,
        }),
      ],
    });
  } else {
    console.warn('⚠️ Sentry is disabled - no DSN provided');
  }
};

// Defer Sentry initialization to reduce critical path latency
// Use requestIdleCallback if available, otherwise wait for load event
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(initSentry, { timeout: 2000 });
} else {
  window.addEventListener('load', initSentry, { once: true });
}

// Defer Mixpanel initialization to reduce critical path latency
// The npm package (mixpanel-browser) is already loaded, we just need to initialize it
const initMixpanel = () => {
  if (mixpanelConfig.token) {
    // The npm package is already available, initialize immediately
    try {
      mixpanel.init(mixpanelConfig.token, {
        debug: mixpanelConfig.debug,
        track_pageview: true,
        persistence: "localStorage",
        autocapture: true,
        record_sessions_percent: 100,
      });
      console.log('[Mixpanel] Initialized successfully');
    } catch (error) {
      console.warn('[Mixpanel] Failed to initialize:', error);
    }
  } else {
    console.warn('⚠️ Mixpanel is disabled - no token provided');
  }
};

// Initialize Mixpanel after page is interactive
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(initMixpanel, { timeout: 2000 });
} else {
  window.addEventListener('load', initMixpanel, { once: true });
}

// Enhanced logging for bundle analysis
console.log('🚀 ChronoCal starting - Code splitting enabled for optimal performance');

// Code splitting enabled for better performance
// Routes are lazy-loaded to reduce initial bundle size

// Performance monitoring in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Development mode - monitoring bundle loading');
  
  const startTime = performance.now();
  
  window.addEventListener('load', () => {
    const loadTime = performance.now() - startTime;
    console.log(`📊 App loaded in ${loadTime.toFixed(2)}ms`);
    
    // Verify NO separate chunks are loaded
    const scripts = Array.from(document.scripts);
    const appScripts = scripts.filter(script => {
      const scriptElement = script as HTMLScriptElement;
      return scriptElement.src && scriptElement.src.includes('/assets/');
    });
    
    console.log('📄 Total scripts loaded:', appScripts.length);
    appScripts.forEach((script, index) => {
      const scriptElement = script as HTMLScriptElement;
      console.log(`Script ${index + 1}:`, scriptElement.src);
    });
    
    // This should only be 1 script (the main bundle)
    if (appScripts.length > 1) {
      console.error('❌ MULTIPLE BUNDLES DETECTED - CONFIGURATION FAILED');
      console.error('❌ Expected: 1 bundle, Got:', appScripts.length);
    } else {
      console.log('✅ Single bundle confirmed - perfect!');
    }
    
    setTimeout(() => {
      const report = performanceMonitor.generateReport();
      console.group('📊 Initial Performance Report');
      console.log('Load time:', `${loadTime.toFixed(2)}ms`);
      console.log('Bundle metrics:', report.bundle);
      console.groupEnd();
    }, 2000);
  });
}

// Enhanced error boundary for chunk loading errors
window.addEventListener('unhandledrejection', (event) => {
  // Suppress Mixpanel autocapture errors to prevent them from breaking the app
  const errorMessage = event.reason?.message || String(event.reason);
  if (
    (typeof errorMessage === 'string' && (
      errorMessage.includes('mixpanel') ||
      errorMessage.includes('Cannot read properties of undefined') ||
      errorMessage.includes('toLowerCase')
    )) ||
    (event.reason?.stack && event.reason.stack.includes('mixpanel'))
  ) {
    console.warn('[Mixpanel] Autocapture error caught and suppressed:', errorMessage);
    event.preventDefault(); // Suppress the error
    return;
  }

  console.error('Unhandled promise rejection:', event.reason);
  
  // Track error in Mixpanel (safely)
  supabase.auth.getUser().then(({ data: { user } }) => {
    safeMixpanelTrack('Error', {
      error_type: 'unhandled_rejection',
      error_message: errorMessage,
      error_code: event.reason?.code || null,
      page_url: window.location.href,
      user_id: user?.id || null,
    });
  }).catch(() => {
    // Silently fail if Supabase auth check fails
  });
  
  if (event.reason?.message?.includes('Failed to fetch dynamically imported module') ||
      event.reason?.message?.includes('LoginPage') ||
      event.reason?.message?.includes('SignUpPage')) {
    console.error('🚨 CHUNK LOADING ERROR - This should be impossible!');
    console.error('🚨 Error details:', event.reason);
    console.error('🚨 All code should be in the main bundle');
    
    // Prevent the error from causing a blank screen
    event.preventDefault();
    
    // Force a reload to try again
    console.log('🔄 Attempting to reload...');
    window.location.reload();
  }
});

// Enhanced error handler for script loading failures
window.addEventListener('error', (event) => {
  // Suppress Mixpanel autocapture errors to prevent them from breaking the app
  if (
    (event.message && typeof event.message === 'string' && (
      event.message.includes('mixpanel') ||
      event.message.includes('Cannot read properties of undefined') ||
      event.message.includes('toLowerCase')
    )) ||
    (event.filename && typeof event.filename === 'string' && event.filename.includes('mixpanel'))
  ) {
    console.warn('[Mixpanel] Autocapture error caught and suppressed:', event.message);
    event.preventDefault(); // Suppress the error
    return;
  }

  // Track error in Mixpanel (safely)
  supabase.auth.getUser().then(({ data: { user } }) => {
    safeMixpanelTrack('Error', {
      error_type: 'script_error',
      error_message: event.message || 'Unknown error',
      error_code: null,
      page_url: window.location.href,
      user_id: user?.id || null,
    });
  }).catch(() => {
    // Silently fail if Supabase auth check fails
  });

  if (event.message?.includes('Failed to fetch dynamically imported module') || 
      event.message?.includes('Loading chunk') ||
      event.filename?.includes('LoginPage-') ||
      event.filename?.includes('SignUpPage-')) {
    console.error('🚨 SCRIPT LOADING ERROR:', event);
    console.error('🚨 This should not happen with single bundle configuration');
    
    // Prevent blank screen
    event.preventDefault();
    
    // Force reload
    console.log('🔄 Reloading due to script error...');
    window.location.reload();
  }
});

// Global cleanup on page unload
window.addEventListener('beforeunload', () => {
  const cleanupManager = CleanupManager.getInstance();
  cleanupManager.cleanup();
  
  if (process.env.NODE_ENV === 'development') {
    const report = performanceMonitor.generateReport();
    console.log('🏁 Final performance report:', report);
  }
});

// Register service worker for offline support
registerServiceWorker().then(() => {
  console.log('📱 PWA capabilities enabled');
}).catch((error) => {
  console.warn('PWA registration failed:', error);
});

// VERIFICATION: Ensure all critical components are accessible
console.log('🔍 Verifying static imports...');
console.log('✅ App component imported statically');
console.log('✅ All pages should be bundled in main chunk');

// Monitor memory usage in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    const cleanupManager = CleanupManager.getInstance();
    const resourceCounts = cleanupManager.getResourceCounts();
    
    if (resourceCounts.abortControllers > 5 || resourceCounts.intervals > 10) {
      console.warn('⚠️ High resource usage detected:', resourceCounts);
    }
  }, 30000);
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
